import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, GripVertical, ListChecks, Plus, Save, Trash2, X } from "lucide-react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Flash } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { catColors, formCats } from "./flashTheme";

const BLANK = { name: "", standard: "", owner: "", goal_pct: 90 };
const COLS = "grid grid-cols-[24px_1.4fr_1.2fr_1fr_72px_148px] gap-2 items-center";

const CatEditor = ({ cats, setCats, testKey }) => (
  <div className="ml-8 mt-2 mb-1 p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2" data-testid={`form-cats-editor-${testKey}`}>
    <div className="text-[11px] text-slate-500">
      Categories (1–4). The <span className="text-emerald-700 font-semibold">first</span> is scored against the goal; the rest show as context. Renaming keeps past numbers; removing a category deletes its past numbers.
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {cats.map((c, i) => (
        <div key={i} className="flex items-center gap-1">
          <span className={`w-2 h-2 rounded-sm ${catColors(cats.length)[i]}`} />
          <Input
            value={c.label}
            onChange={(e) => setCats(cats.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
            className="h-8 w-36 text-sm bg-white"
            data-testid={`form-cat-${i}-${testKey}`}
          />
          {cats.length > 1 && (
            <button onClick={() => setCats(cats.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`form-cat-remove-${i}-${testKey}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      {cats.length < 4 && (
        <Button size="sm" variant="outline" onClick={() => setCats([...cats, { label: "" }])} className="h-8 bg-white" data-testid={`form-cat-add-${testKey}`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Category
        </Button>
      )}
    </div>
  </div>
);

const FormRow = ({ form, onSave, onDelete, onToggle, testKey, handle = <span />, rowRef, rowStyle, dragging }) => {
  const [v, setV] = useState({ ...form, categories: formCats(form) });
  const [showCats, setShowCats] = useState(false);
  useEffect(() => setV({ ...form, categories: formCats(form) }), [form]);
  const catLabels = (c) => JSON.stringify(c.map((x) => x.label));
  const dirty = ["name", "standard", "owner", "goal_pct"].some((k) => String(v[k] ?? "") !== String(form[k] ?? "")) || catLabels(v.categories) !== catLabels(formCats(form));
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  return (
    <div
      ref={rowRef}
      style={rowStyle}
      className={`py-2 border-b border-slate-100 bg-white ${form.hidden ? "opacity-60" : ""} ${dragging ? "relative z-10 shadow-lg rounded-lg ring-1 ring-sky-200" : ""}`}
      data-testid={`manage-form-row-${testKey}`}
    >
    <div className={COLS}>
      {handle}
      <Input value={v.name} onChange={set("name")} placeholder="Form name" className="h-9" data-testid={`form-name-${testKey}`} />
      <Input value={v.standard} onChange={set("standard")} placeholder="e.g. Within 8 hrs" className="h-9" data-testid={`form-standard-${testKey}`} />
      <Input value={v.owner} onChange={set("owner")} placeholder="Owner / group" className="h-9" data-testid={`form-owner-${testKey}`} />
      <Input type="number" min="0" max="100" value={v.goal_pct} onChange={set("goal_pct")} className="h-9 font-mono" data-testid={`form-goal-${testKey}`} />
      <div className="flex gap-1 justify-end">
        <Button size="icon" variant="ghost" onClick={() => setShowCats((s) => !s)} title="Edit categories" className={`h-8 w-8 ${showCats ? "bg-sky-50 text-sky-700" : "text-slate-500"}`} data-testid={`form-cats-toggle-${testKey}`}>
          <ListChecks className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" disabled={!dirty || !v.name?.trim()} onClick={() => onSave(v)} className="h-8 w-8 text-sky-700" data-testid={`form-save-${testKey}`}>
          {form.id ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </Button>
        {form.id && (
          <Button size="icon" variant="ghost" onClick={() => onToggle(form)} title={form.hidden ? "Hidden from dashboard & PDF, click to show" : "Shown, click to hide"} className={`h-8 w-8 ${form.hidden ? "text-slate-400" : "text-emerald-600"}`} data-testid={`form-visibility-${testKey}`}>
            {form.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
        )}
        {form.id && (
          <Button size="icon" variant="ghost" onClick={() => onDelete(form)} className="h-8 w-8 text-rose-600 hover:bg-rose-50" data-testid={`form-delete-${testKey}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
      {showCats && <CatEditor cats={v.categories} setCats={(c) => setV({ ...v, categories: c })} testKey={testKey} />}
    </div>
  );
};

const SortableRow = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.form.id });
  const handle = (
    <button
      {...attributes}
      {...listeners}
      className="h-8 w-6 flex items-center justify-center text-slate-400 hover:text-sky-700 cursor-grab active:cursor-grabbing touch-none"
      title="Drag to reorder"
      data-testid={`form-drag-${props.form.id}`}
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
  return <FormRow {...props} handle={handle} rowRef={setNodeRef} rowStyle={{ transform: CSS.Transform.toString(transform), transition }} dragging={isDragging} />;
};

export const FlashFormsDialog = ({ open, onOpenChange, forms, onChanged }) => {
  const [order, setOrder] = useState(forms);
  useEffect(() => setOrder(forms), [forms]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    }
  };
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(order, order.findIndex((f) => f.id === active.id), order.findIndex((f) => f.id === over.id));
    setOrder(next);
    try {
      await Flash.reorderForms(next.map((f) => f.id));
      onChanged();
    } catch {
      setOrder(forms);
      toast.error("Could not save the new order");
    }
  };
  const save = (v) => {
    const goal = Number(v.goal_pct);
    if (v.goal_pct === "" || Number.isNaN(goal) || goal < 0 || goal > 100) {
      toast.error("Goal must be a number from 0 to 100");
      return;
    }
    if (v.categories.some((c) => !c.label.trim())) {
      toast.error("Every category needs a name");
      return;
    }
    const payload = { name: v.name, standard: v.standard, owner: v.owner, goal_pct: goal, categories: v.categories.map((c) => ({ key: c.key, label: c.label.trim() })) };
    return v.id
      ? run(() => Flash.updateForm(v.id, payload), `Updated ${v.name}`)
      : run(() => Flash.createForm(payload), `Added ${v.name}`);
  };
  const toggle = (f) => run(() => Flash.updateForm(f.id, { hidden: !f.hidden }), f.hidden ? `${f.name} is now shown` : `${f.name} hidden from dashboard & PDF`);
  const remove = (f) => {
    if (!window.confirm(`Delete "${f.name}" and all of its entered metrics?`)) return;
    run(() => Flash.removeForm(f.id), `Deleted ${f.name}`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white" data-testid="manage-forms-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Manage forms</DialogTitle>
          <DialogDescription>
            Add, rename or remove forms, set owners and goals. Drag the handle to set the order on the page and PDF. Use the eye to show or hide a form; hidden forms keep their data.
          </DialogDescription>
        </DialogHeader>
        <div className={`${COLS} text-[11px] uppercase tracking-wider text-slate-500 font-heading font-semibold`}>
          <span /><span>Form</span><span>Standard</span><span>Owner</span><span>Goal %</span><span />
        </div>
        <div className="max-h-[55vh] overflow-y-auto -mt-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={order.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              {order.map((f) => <SortableRow key={f.id} form={f} testKey={f.id} onSave={save} onDelete={remove} onToggle={toggle} />)}
            </SortableContext>
          </DndContext>
          <div className="pt-3 text-[11px] uppercase tracking-wider text-sky-700 font-heading font-semibold">Add a form</div>
          <FormRow key={`new-${forms.length}`} form={BLANK} testKey="new" onSave={save} onDelete={remove} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
